/**
 * src/engine/monteCarlo.ts — stochastic projection (§6.18). Runs the deterministic
 * monthly engine many times with random portfolio returns (one-factor cross-asset
 * correlation) and optional stochastic SOFR. Reproducible for a fixed seed.
 */
import type {
  MonteCarloConfig,
  MonteCarloResult,
  Percentile,
  ScenarioInputs,
} from '@/types';
import { type BucketState } from './collateral';
import { createRng, type Rng } from './prng';
import { runProjection } from './projection';

/** Sample per-bucket MONTHLY returns under a one-factor equicorrelation model (§6.18). */
export function sampleReturns(
  states: BucketState[],
  config: Pick<MonteCarloConfig, 'volatilityPct' | 'lognormal' | 'crossAssetCorrelation'>,
  useBuckets: boolean,
  rng: Rng,
): number[] {
  const rho = Math.min(1, Math.max(0, config.crossAssetCorrelation));
  const sqrtRho = Math.sqrt(rho);
  const sqrtOneMinus = Math.sqrt(1 - rho);
  const zMarket = rng.normal();
  return states.map((b) => {
    const mean = b.expectedReturnAnnualPct / 100 / 12;
    const volAnnual = (useBuckets ? b.volatilityPct : config.volatilityPct) / 100;
    const sd = volAnnual / Math.sqrt(12);
    const z = sqrtRho * zMarket + sqrtOneMinus * rng.normal();
    if (config.lognormal) return Math.exp(mean - 0.5 * sd * sd + sd * z) - 1;
    return mean + sd * z;
  });
}

/** Mean-reverting stochastic SOFR sampler (§6.18), floored at 0 by the projection loop. */
function makeBaseRateSampler(baseRatePct: number, rng: Rng): (m: number, prev: number) => number {
  const kappa = 0.1;
  const theta = baseRatePct;
  const sigma = 0.3;
  return (_m, prev) => prev + kappa * (theta - prev) + sigma * rng.normal();
}

/** q-quantile of a sorted array via linear interpolation. */
export function quantile(sorted: number[], q: number): number {
  if (sorted.length === 0) return 0;
  if (sorted.length === 1) return sorted[0]!;
  const idx = q * (sorted.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const frac = idx - lo;
  return sorted[lo]! + (sorted[hi]! - sorted[lo]!) * frac;
}

/** The 5/25/50/75/95 percentile band of a sample. */
export function percentiles(sample: number[]): Record<Percentile, number> {
  const sorted = sample.slice().sort((a, b) => a - b);
  return {
    p5: quantile(sorted, 0.05),
    p25: quantile(sorted, 0.25),
    p50: quantile(sorted, 0.5),
    p75: quantile(sorted, 0.75),
    p95: quantile(sorted, 0.95),
  };
}

/** Run the Monte Carlo simulation (§6.18). `onProgress` reports fraction complete [0,1]. */
export function runMonteCarlo(
  inputs: ScenarioInputs,
  config: MonteCarloConfig,
  onProgress?: (fraction: number) => void,
): MonteCarloResult {
  const months = inputs.years * 12;
  const rng = createRng(config.seed);
  const mcInputs: ScenarioInputs = { ...inputs, modelForcedLiquidation: true };

  const netWorthByMonth: number[][] = Array.from({ length: months }, () => []);
  const terminalNetWorth: number[] = [];
  const terminalLoan: number[] = [];
  const terminalHeirs: number[] = [];
  let callCount = 0;
  const progressEvery = Math.max(1, Math.floor(config.runs / 100));

  for (let run = 0; run < config.runs; run++) {
    const baseRateSampler = config.stochasticBaseRate
      ? makeBaseRateSampler(inputs.ratePath.baseRatePct, rng)
      : undefined;
    const borrow = runProjection(mcInputs, {
      returnSampler: (_m, states) => sampleReturns(states, config, inputs.useBuckets, rng),
      baseRateSampler,
    });

    let called = false;
    for (let m = 0; m < months; m++) {
      const row = borrow.rows[m]!;
      netWorthByMonth[m]!.push(row.netWorth);
      if (row.maintenanceCall) called = true;
    }
    if (called) callCount++;

    const last = borrow.rows[months - 1]!; // months ≥ 12
    const portfolioB = borrow.finalStates.reduce((s, b) => s + b.value, 0);
    const loan = last.endBalance;
    const estateValueBorrow = portfolioB - loan;
    const estateTaxB = inputs.tax.estateTaxEnabled
      ? (Math.max(0, estateValueBorrow - inputs.tax.estateExemption) * inputs.tax.estateTaxRatePct) / 100
      : 0;
    terminalNetWorth.push(last.netWorth);
    terminalLoan.push(loan);
    terminalHeirs.push(estateValueBorrow - estateTaxB);

    if (onProgress && run % progressEvery === 0) onProgress((run + 1) / config.runs);
  }
  onProgress?.(1);

  return {
    runs: config.runs,
    seed: config.seed,
    maintenanceCallProbability: config.runs > 0 ? callCount / config.runs : 0,
    netWorthPercentiles: netWorthByMonth.map((arr, m) => ({ monthIndex: m, ...percentiles(arr) })),
    terminal: {
      netWorth: percentiles(terminalNetWorth),
      loanBalance: percentiles(terminalLoan),
      heirsValue: percentiles(terminalHeirs),
    },
  };
}
